//const API_BASE = '/api/audit';
const API_BASE = (import.meta.env.VITE_API_BASE_URL || 'https://localhost:7188/api') + '/audit'

export const fetchAuditTrail = async ({
  page = 1,
  pageSize = 25,
  userFilter = null,
  codeFilter = null,
  fromDate = null,
  toDate = null
} = {}) => {
  const params = new URLSearchParams({
    page: page.toString(),
    pageSize: pageSize.toString()
  });

  if (userFilter) params.append('userFilter', userFilter);
  if (codeFilter) params.append('codeFilter', codeFilter);
  if (fromDate) params.append('fromDate', fromDate);
  if (toDate) params.append('toDate', toDate);

  const response = await fetch(`${API_BASE}?${params}`, {
    credentials: 'include'
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch audit trail: ${response.status}`);
  }

  return response.json();
};

export const fetchAuditCodes = async () => {
  const response = await fetch(`${API_BASE}/codes`, {
    credentials: 'include'
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch audit codes: ${response.status}`);
  }

  return response.json();
};

export const logAuditEvent = async ({ auditCode, annotation = null, entityId = null, entityType = null }) => {
  try {
    const response = await fetch(`${API_BASE}/log`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ auditCode, annotation, entityId, entityType })
    });

    if (!response.ok) {
      return false;
    }
    return true;
  } catch (error) {
    return false; // Silent fail for audit logging
  }
};

// ============ CENTRALIZED AUDIT FUNCTIONS ============

// Pre-defined audit codes for consistency
export const AUDIT_CODES = {
  USER_LOGIN: "AUD001",
  USER_LOGOUT: "AUD002",
  DASHBOARD_ACCESSED: "AUD003",
  ZONES_LIST_VIEWED: "AUD004",
  ZONE_CREATED: "AUD005",
  ZONE_EDITED: "AUD006",
  ZONE_DELETED: "AUD007",
  FORMS_LIST_VIEWED: "AUD008",
  FORM_CREATED: "AUD009",
  FORM_EDITED: "AUD010",
  FORM_DELETED: "AUD011",
  INSPECTIONS_LIST_VIEWED: "AUD012",
  INSPECTION_CREATED: "AUD013",
  INSPECTION_EDITED: "AUD014",
  INSPECTION_COMPLETED: "AUD015",
  TASKS_LIST_VIEWED: "AUD016",
  TASK_CREATED: "AUD017",
  TASK_ASSIGNED: "AUD018",
  TASK_COMPLETED: "AUD019",
  FINDINGS_LIST_VIEWED: "AUD020",
  FINDING_CREATED: "AUD021",
  FINDING_RESOLVED: "AUD022",
  EMAIL_2FA_CODE_SENT: "AUD023",
  TWO_FACTOR_LOGIN_SUCCESS: "AUD024",
  TWO_FACTOR_LOGIN_FAILED: "AUD025",
  CUSTOMER_SETTINGS_VIEWED: "AUD026",
  CUSTOMER_SETTINGS_UPDATED: "AUD027",
  CUSTOMER_LOGO_UPDATED: "AUD028",
  CONTRACTORS_LIST_VIEWED: "AUD029",
  CONTRACTOR_CREATED: "AUD030",
  CONTRACTOR_EDITED: "AUD031",
  CONTRACTOR_DELETED: "AUD032",
  WORK_ORDERS_LIST_VIEWED: "AUD033",
  WORK_ORDER_CREATED: "AUD034",
  WORK_ORDER_EDITED: "AUD035",
  WORK_ORDER_ASSIGNED: "AUD036",
  WORK_ORDER_COMPLETED: "AUD037",
  WORK_ORDER_DELETED: "AUD038",
  WORK_ORDER_ASSIGNMENT_EMAIL_RESENT: "AUD039",
  PHOTO_UPLOADED: "AUD040",
  PHOTO_DELETED: "AUD041",
  WORK_ORDERS_BATCH_MOVED: "AUD042",
  REPORTS_VIEWED: "AUD043",
  REPORT_TEMPLATES_VIEWED: "AUD044",
  REPORT_TEMPLATE_CREATED: "AUD045",
  REPORT_TEMPLATE_EDITED: "AUD046",
  REPORT_TEMPLATE_DELETED: "AUD047",
  EMAIL_TEMPLATES_VIEWED: "AUD048",
  EMAIL_TEMPLATE_CREATED: "AUD049",
  EMAIL_TEMPLATE_EDITED: "AUD050",
  EMAIL_TEMPLATE_DELETED: "AUD051",
  MAP_OVERLAYS_SETTINGS_VIEWED: "AUD052",
  MAP_OVERLAYS_VIEWED: "AUD053",
  OVERLAY_CREATED: "AUD054",
  OVERLAY_EDITED: "AUD055",
  OVERLAY_DELETED: "AUD056",
  WEATHER_VIEWED: "AUD057",
  WEATHER_POINT_CREATED: "AUD058",
  WEATHER_POINT_DELETED: "AUD059",
  WEATHER_CAPTURE_TRIGGERED: "AUD060",
  AUDIT_TRAIL_VIEWED: "AUD061",
  TEAM_USERS_VIEWED: "AUD062"
};

// Centralized logging functions
export const auditLog = {
  async routeViewed(auditCode, annotation, entityType = "navigation") {
    return logAuditEvent({
      auditCode,
      annotation,
      entityType,
    });
  },

  async contractorAddClicked(context = 'Customer Settings') {
    return false;
  },

  async contractorCreated(contractorId = null, contractorName = null) {
    return false;
  },

  // Generic custom logging
  async custom(auditCode, annotation, entityType = null, entityId = null) {
    return logAuditEvent({
      auditCode,
      annotation,
      entityType,
      entityId: entityId?.toString()
    });
  }
};
